import { useCallback, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "store";
import { notificationsService } from "api/notifications";
import { setNotifications } from "store/notificationsSlice";

// Page 1 mirrors the panel's own page size (NotificationsList.tsx) so the first "load more" scroll
// continues cleanly from where the boot fetch left off.
const BOOT_PAGE_SIZE = 20;

// How often we re-check the unread count while the user has at least one BE job in flight. This is
// plain interval polling on purpose (CLAUDE.md: this is unread-count polling, not a job — the
// per-job completion signal is the WS `notification` key handled in SocketListener, not this hook).
const ACTIVE_JOBS_POLL_INTERVAL = 60_000;

// Boots the Notifications feed (REF-6306): fetches page 1 once the user is authorized, then keeps
// the unread badge honest via two independent mechanisms that both stay on even after the initial
// fetch completes:
//   - focus-sync: re-check the unread count whenever the tab regains focus or becomes visible again
//     (catches anything missed while the tab was backgrounded — WS frames don't fire for hidden tabs
//     on every browser).
//   - heartbeat: while `GET /jobs/active` reports at least one active job for this user, also poll
//     the unread count every 60s, so the badge still updates even if a completion WS frame is
//     dropped or the socket never reconnects.
//
// Mounted once from Layout, next to useWhatsNewBoot.
const useNotificationsBoot = () => {
  const dispatch = useAppDispatch();
  const isAuthorized = useAppSelector((s) => s.common.isAuthorized);
  const startedRef = useRef(false);

  // Boot fetch — page 1 of the feed, once.
  useEffect(() => {
    if (!isAuthorized || startedRef.current) return;
    startedRef.current = true;

    (async () => {
      try {
        const feedRes = await notificationsService.getFeed({ page: 1, page_size: BOOT_PAGE_SIZE });

        if (Array.isArray(feedRes?.items)) {
          dispatch(
            setNotifications({
              items: feedRes.items,
              page: feedRes.pagination?.page ?? 1,
              hasMore: feedRes.pagination?.has_more ?? false,
              unreadCount: feedRes.unread_count ?? 0,
            })
          );
        }
      } catch {
        /* notifications feed unavailable (network / not-yet-migrated BE) — leave the empty initial state */
      }
    })();
  }, [isAuthorized, dispatch]);

  // Reconcile: page 1 of the FEED, and the badge with it.
  //
  // This replaced a count-only refresh (REF-6509, from a real dev report). Both recovery paths —
  // focus-sync and the heartbeat — used to read `getUnreadCount()` alone, and `NotificationsList`
  // only ever pages FORWARD, so nothing in the app re-read page 1 after the one boot fetch. A
  // notification whose WS frame never arrived therefore moved the BADGE to 1 and left the LIST
  // without the row, until a reload. The badge said "you have one" and the panel disagreed.
  //
  // Page 1 is the whole fix: the row is newest-first by construction, `setNotifications` replaces
  // the page-1 window, and the response already carries `unread_count`, so this is ONE request
  // where the old code made one for the count alone. Later pages are dropped on purpose — they
  // are re-fetched by the same `loadMore` scroll that loaded them.
  const reconcileFeed = useCallback(async () => {
    try {
      const res = await notificationsService.getFeed({ page: 1, page_size: BOOT_PAGE_SIZE });
      if (!Array.isArray(res?.items)) return;

      dispatch(
        setNotifications({
          items: res.items,
          page: res.pagination?.page ?? 1,
          hasMore: res.pagination?.has_more ?? false,
          unreadCount: res.unread_count ?? 0,
        })
      );
    } catch {
      /* best-effort — the next focus / tick tries again */
    }
  }, [dispatch]);

  // Focus-sync.
  useEffect(() => {
    if (!isAuthorized) return;

    const handleFocus = () => {
      void reconcileFeed();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") void reconcileFeed();
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthorized, reconcileFeed]);

  // Heartbeat: the fallback for a notification whose WS frame never arrived.
  //
  // `/jobs/active` is the DISCOVERY call and cannot be gated on "a job is active" — that is the
  // question it exists to answer, and this hook has no way to know a job started somewhere else
  // (another tab, another surface, a Cloud Task retry). So it runs for the authorized session,
  // but only while the tab is VISIBLE: a hidden tab has no badge to keep honest (there is no
  // document.title or favicon counter anywhere in the app), and the focus-sync effect above
  // reconciles the feed the instant it comes back — so nothing is lost, only deferred. Chrome's
  // background-timer throttling floor is itself 60 s, so backgrounding does not attenuate this
  // interval on its own; without the gate a forgotten tab polls a documented 4-source projection
  // (`GET /v1/jobs/active` unions job_queue + studio_project + brand_dna + trend_analysis) every
  // minute for as long as it stays open.
  //
  // What WAS a correctness bug (REF-6509): `count <= 0` returned before refreshing the unread
  // count, so the one tick that mattered most — the transition from "a job is running" to "none
  // are", i.e. the moment its notification was written — was exactly the tick that skipped the
  // read. The fallback declined to fire at the only instant it had something to catch.
  //
  // The count is compared against the previous tick's, so the refresh happens while jobs are
  // running AND once on the edge down to zero. An idle session that has seen nothing still makes
  // no unread request at all, which is what the early return was reaching for.
  const previousActiveJobsRef = useRef(0);
  // Set whenever a tick was skipped for a hidden tab, because the skip leaves
  // `previousActiveJobsRef` behind: a job that both STARTED and FINISHED while hidden is never
  // seen as positive, so the positive→zero edge below never fires and its notification is missed.
  // The first visible tick therefore reconciles once unconditionally, then resumes edge detection.
  const baselineStaleRef = useRef(false);
  useEffect(() => {
    if (!isAuthorized) return;

    const tick = async () => {
      // See the note above: a hidden tab's heartbeat buys nothing and costs a 4-source projection.
      if (document.visibilityState === "hidden") {
        baselineStaleRef.current = true;

        return;
      }

      try {
        const activeJobs = await notificationsService.getActiveJobs();
        const count = activeJobs?.count ?? 0;
        const baselineStale = baselineStaleRef.current;
        baselineStaleRef.current = false;
        const settledSinceLastTick = count <= 0 && previousActiveJobsRef.current > 0;
        previousActiveJobsRef.current = count;
        if (count <= 0 && !settledSinceLastTick && !baselineStale) return;

        // The FEED, not just the count — see `reconcileFeed`. The tick that matters is the one
        // where a job just settled, and on that tick the row itself is what the user is waiting
        // for; a badge that goes to 1 over an empty list is the bug this replaced.
        await reconcileFeed();
      } catch {
        /* best-effort heartbeat — never surface an error for this */
      }
    };

    const intervalId = setInterval(tick, ACTIVE_JOBS_POLL_INTERVAL);

    return () => clearInterval(intervalId);
  }, [isAuthorized, reconcileFeed]);
};

export default useNotificationsBoot;
